<template>
  <!-- Template when page licence requirement is not met -->
  <div v-if="!licenced(node) || !featureEnabled(node) || !authorized(node)"></div>

  <!-- Template when overide specified on route node -->
  <component v-else-if="node.meta && node.meta.overrides && node.meta.overrides.sidebar"
    :is="node.meta.overrides.sidebar" :node="node">
  </component>

  <!-- Template when overide set to false on route node -->
  <div v-else-if="node.meta && node.meta.overrides && node.meta.overrides.sidebar === false"></div>

  <!-- Normal template -->
  <v-container v-else fluid class="pa-0">
    <v-list-item dense link class="node pr-2" :class="!hover || 'hover'" active-class="child active"
      exact-active-class="self active" :to="{
        name: node.name,
        params: node.meta && node.meta.params ? node.meta.params : undefined,
        query: node.meta && node.meta.query ? node.meta.query : undefined
      }" @click="onlyIconMode ? (open = true) : null" @mouseenter="hover = true" @mouseleave="hover = false">
      <v-list-item-icon class="my-auto mr-4">
        <slot name="icon" :icontext="routeIcon(node)">
          <v-badge dot offset-y="7" bottom :color="onlyIconMode && node.count > 0 ? 'primary' : 'transparent'">
            <v-icon size="22" class="node-icon">
              <slot name="icon-text">{{ routeIcon(node) }}</slot>
            </v-icon>
          </v-badge>
        </slot>
      </v-list-item-icon>
      <v-list-item-content>
        <slot name="text">
          <v-list-item-title class="node-text">
            <slot name="text-value">{{ routeDisplayName(node) }}</slot>
          </v-list-item-title>
        </slot>
      </v-list-item-content>
      <v-list-item-action class="my-auto d-flex flex-row">
        <slot name="count">
          <CardBadge v-if="routeCount(node)" :color="'primary'" :count="routeCount(node)">
          </CardBadge>
        </slot>
      </v-list-item-action>
      <v-list-item-action class="my-auto d-flex flex-row" style="min-width: 0px;" :class="Children ? 'ml-0' : 'ml-2'">
        <slot name="actions">
          <v-tooltip v-if="Children" bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on" @click.prevent="open = !open">
                <v-icon v-text="!open ? 'mdi-chevron-down' : 'mdi-chevron-up'"></v-icon>
              </v-btn>
            </template>
            <span v-text="!open ? 'Expand' : 'Collapse'"></span>
          </v-tooltip>
        </slot>
      </v-list-item-action>
    </v-list-item>
    <div v-if="Children" v-show="open" :class="'indent'">
      <slot name="children">
        <template v-for="(childroute, index) in Children">
          <RouteListItem :key="index" :node="childroute">
          </RouteListItem>
        </template>
      </slot>
    </div>
  </v-container>
</template>


<script>
import CardBadge from "@/components/Buttons/CardBadge.vue";

export default {
  name: "RouteListItem_Base",
  props: {
    node: Object,
    openfunc: {
      type: Function,
      default: function () {
        return this.$route.matched.some(({ name }) => name == this.node.name);
      },
    },
  },
  components: {
    RouteListItem: () => import("@/components/SingleUse/ComponentOverrides/sidebar/RouteListItem_Base.vue"),
    CardBadge,
  },

  data() {
    return {
      open: false,
      hover: false,
    };
  },

  mounted() {
    setTimeout(() => {
      this.open = this.openfunc();
    }, 700);
  },

  methods: {
    authorized(node) {
      return this.checkUserAuthorizationForNode(node);
    },
    licenced(node) {
      return this.checkIfLicencedByKey(node.meta?.licenseEnabled);
    },
    featureEnabled(node) {
      return this.checkIfFeatureEnabledByKey(node.meta?.featureEnabled);
    },
  },

  computed: {
    onlyIconMode() {
      if (!this.isPadBool) {
        return !this.$store.getters["navstore/Extended"];
      } else {
        return false;
      }
    },

    Children() {
      if (this.node.children && this.node.children[0] && this.authorized(this.node) && this.licenced(this.node) && this.featureEnabled(this.node)) {
        if (this.node.children.some((x) => !x.meta || !x.meta.overrides || x.meta.overrides.sidebar !== false)) {
          return this.node.children;
        }
      }
      return false;
    },
  },

  watch: {
    open(newValue) {
      if (newValue) {
        this.$emit('open')
      }
    },
  },
};
</script>


<style lang='scss'>
/*--------- indent ----------*/
/*full mode*/
.indent {
  margin-left: 25px;
  position: relative;
}

.indent::before {
  content: "";
  position: absolute;
  height: 100%;
  width: 3px;
  border-radius: 3px;
  left: 0px;
  z-index: 3;
  background-color: rgb(224, 224, 224);
}

/*icon only mode*/
.v-navigation-drawer--mini-variant .indent {
  margin-left: 0px;
  background-color: rgba($color: #000000, $alpha: 0.06);
}

.v-navigation-drawer--mini-variant .indent::before {
  border-radius: 0px;
  background-color: rgba($color: #000000, $alpha: 0.3);
}

/*------- indent end --------*/

/*---------- node -----------*/
.node {
  color: transparent !important;
}

.node-text,
.node-icon {
  color: var(--v-navigationtext-base);
}

.node.hover .node-text,
.node.hover .node-icon {
  color: var(--v-tertiary-base);
}

.v-navigation-drawer--mini-variant .node {
  padding-left: 13px;
  border-left: 3px solid transparent;
}

/*parent of active node*/
.node.child.active .node-text,
.node.child.active .node-icon {
  color: var(--v-primarytext-darken2) !important;
}

.v-navigation-drawer:not(.v-navigation-drawer--mini-variant) .node.active::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: var(--v-navigationdrawer-active-base);
  opacity: 0.6;
  z-index: -1;
}

.v-navigation-drawer:not(.v-navigation-drawer--mini-variant) .node.self.active::after {
  opacity: 0.9;
}

.v-navigation-drawer:not(.v-navigation-drawer--mini-variant) .node.active::before {
  content: "";
  color: var(--v-primary-darken2) !important;
  width: 3px;
  left: 0px;
  z-index: 11;
  opacity: 1 !important;
  border-radius: 3px;
}

.v-navigation-drawer:not(.v-navigation-drawer--mini-variant) .node.self.active::before {
  color: var(--v-primary-lighten1) !important;
}

.v-navigation-drawer--mini-variant .node.child.active {
  z-index: 10;
  border-color: var(--v-primary-darken2);
  box-shadow: rgb(155, 155, 155) 0px 3px 3px;
  background-color: var(--v-navigationdrawer-active-base);
}

/*active node*/
.node.self.active .node-text,
.node.self.active .node-icon {
  color: var(--v-primary-base) !important;
}

.v-navigation-drawer--mini-variant .node.self.active {
  z-index: 11;
  border-color: var(--v-primary-base);
  box-shadow: rgb(155, 155, 155) 0px 4px 4px;
  background-color: var(--v-navigationdrawer-active-lighten1);
}

/*-------- node end ---------*/

@media only screen and (max-width: 1400px) and (min-width: 1000px) {
  .v-navigation-drawer:not(.v-navigation-drawer--mini-variant) .indent {
    margin-left: 18px;
  }

  .v-navigation-drawer .node {
    min-height: 32px !important;
    padding-left: 8px !important;
    padding-right: 0px !important;
  }

  .v-navigation-drawer--mini-variant .node {
    padding-left: 8px !important;
  }

  .v-navigation-drawer .node .node-text {
    font-size: 12.5px !important;
  }

  .v-navigation-drawer:not(.v-navigation-drawer--mini-variant) .node .v-list-item__icon {
    margin-right: 10px !important;
    padding-top: 2px;
  }

  .v-navigation-drawer .node .node-icon {
    font-size: 16px !important;
  }

  .v-navigation-drawer:not(.v-navigation-drawer--mini-variant) .node .card-badge {
    transform: scale(0.85) !important;
  }
}
</style>